package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.core.Page

@Page("/kmm/firebase")
@Composable
fun KMPFirebasePage() {
    BlogLayout(
        PageProperties(
            "Firebase",
            ""
        ),
        BlogProperties("KMP", "Firebase")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
inline fun <reified T> FirebaseFirestore.getFirestoreCollection(collectionName: String): Flow<List<T>> =
    this.collection(collectionName)
        .snapshots
        .map { querySnapshot ->
            querySnapshot.documents.map { it.data() }
        }

inline fun <reified T> FirebaseFirestore.getFirestoreCollectionByField(
    collectionName: String,
    fieldName: String,
    fieldValue: Any
): Flow<T?> =
    this.collection(collectionName)
        .where { fieldName equalTo fieldValue }
        .snapshots
        .map { querySnapshot ->
            querySnapshot.documents
                .map<DocumentSnapshot, T> { it.data() }
                .firstOrNull()
        }

inline fun <reified T> FirebaseFirestore.getFirestoreDocument(
    collectionName: String,
    documentId: String,
): Flow<T?> =
    this.collection(collectionName)
        .document(documentId)
        .snapshots
        .map { it.data() }

inline fun <reified T> FirebaseFirestore.getFirestoreFirstAvailableDocument(
    collectionName: String
): Flow<T?> =
    this.collection(collectionName)
        .snapshots
        .map { querySnapshot ->
            querySnapshot.documents.firstOrNull()?.data()
        }

suspend inline fun <reified T> FirebaseFirestore.saveObject(
    collectionName: String,
    id: String,
    data: T
) =
    this.collection(collectionName)
        .document(id)
        .set(data)

suspend inline fun FirebaseFirestore.deleteObject(
    collectionName: String,
    id: String,
) =
    this.collection(collectionName)
        .document(id)
        .delete()

suspend inline fun FirebaseFirestore.checkIfDocumentExists(
    collectionName: String,
    documentId: String,
) =
    this.collection(collectionName)
        .document(documentId)
        .get()
        .exists
                    """.trimIndent()
            )
        }

        KotlinCode(
            code = """
                expect fun getFirebaseFirestore(): FirebaseFirestore
                
                val appModule = module {
                    single { getFirebaseFirestore() }
                }
                
                //androidMain
                import com.google.firebase.Firebase
                import com.google.firebase.firestore.firestore
                import dev.gitlive.firebase.firestore.FirebaseFirestore

                actual fun getFirebaseFirestore(): FirebaseFirestore {
                    return FirebaseFirestore(android = Firebase.firestore(database = "s11-prod"))
                }
                
                //iosMain
                import cocoapods.FirebaseFirestore.*
                import dev.gitlive.firebase.firestore.FirebaseFirestore

                var globalFireStore: FIRFirestore? = null

                actual fun getFirebaseFirestore(): FirebaseFirestore {
                    // FIRFirestore.firestoreForDatabase("") // preview feature not supported yet
                    // Firestore.firestore(database: "s11-prod")
                    // https://firebase.google.com/docs/reference/swift/firebasefirestore/api/reference/Classes/Firestore#/c:objc(cs)FIRFirestore(cm)firestoreForApp:database:

                    return FirebaseFirestore(ios = globalFireStore ?: FIRFirestore.firestore())
                }
                
                import dev.gitlive.firebase.firestore.DocumentSnapshot
                import dev.gitlive.firebase.firestore.FirebaseFirestore
                import kotlinx.coroutines.flow.Flow
                import kotlinx.coroutines.flow.map

                abstract class FireStoreBaseDataSource<T>(
                    private val firestore: FirebaseFirestore,
                ) {
                    protected abstract val collectionPath: String
                    protected abstract val mapper: (DocumentSnapshot) -> T

                    protected suspend fun getData(): List<T> {
                        val snapshotResponse = firestore.collection(collectionPath = collectionPath).get()

                        return snapshotResponse.documents.map {
                            mapper(it)
                        }
                    }

                    protected fun getDataFlow(): Flow<List<T>> {
                        val snapshotResponse = firestore.collection(collectionPath = collectionPath).snapshots

                        return snapshotResponse.map  { querySnapshot ->
                            querySnapshot.documents.map { documentSnapshot ->
                                mapper(documentSnapshot)
                            }
                        }
                    }
                }
                
                import kotlinx.serialization.Serializable

                @Serializable
                data class Faq(
                    val answer: String,
                    val question: String,
                )
                
                import com.sjaindl.s11.core.firestore.FireStoreBaseDataSource
                import dev.gitlive.firebase.firestore.DocumentSnapshot
                import dev.gitlive.firebase.firestore.FirebaseFirestore
                import kotlinx.coroutines.flow.Flow

                interface FaqDataSource {
                    suspend fun getFaqs(): List<Faq>
                    fun getFaqsFlow(): Flow<List<Faq>>
                }

                internal class FaqDataSourceImpl(
                    firestore: FirebaseFirestore,
                ): FireStoreBaseDataSource<Faq>(firestore = firestore), FaqDataSource {

                    override val collectionPath: String = "faq"

                    override val mapper: (DocumentSnapshot) -> Faq = {
                        it.data()
                    }

                    override suspend fun getFaqs() = getData()

                    override fun getFaqsFlow() = getDataFlow()
                }
                
                import kotlinx.coroutines.flow.Flow

                interface FaqRepository {
                    suspend fun getFaqs(): List<Faq>
                    fun getFaqsFlow(): Flow<List<Faq>>
                }

                class FaqRepositoryImpl(
                    private val playerDataSource: FaqDataSource,
                ): FaqRepository {
                    override suspend fun getFaqs() = playerDataSource.getFaqs()

                    override fun getFaqsFlow() = playerDataSource.getFaqsFlow()
                }
            """.trimIndent()
        )

        //https://github.com/KevinBlock-GC/kmp-firebase-auth
        KotlinCode(
            code = $$"""
                [versions]
                agp = "8.10.1"
                android-compileSdk = "36"
                android-minSdk = "24"
                android-targetSdk = "36"
                androidx-activity = "1.10.1"
                androidx-lifecycle = "2.9.0"
                composeMultiplatform = "1.8.1"
                kotlin = "2.1.21"
                firebase-bom = "33.14.0"
                google-services-plugin = "4.4.2"
                koin-bom = "4.1.0-RC1"
                coroutines = "1.10.2"
                navigation-compose = "2.9.0-beta02"
                playServicesAuth = "21.3.0"
                kotlin-serialization-json = "1.8.1"
                materialIcons = "1.7.3"

                [libraries]
                kotlin-test = { module = "org.jetbrains.kotlin:kotlin-test", version.ref = "kotlin" }
                kotlinx-coroutines-core = { module = "org.jetbrains.kotlinx:kotlinx-coroutines-core", version.ref = "coroutines" }
                kotlinx-coroutines-android = { module = "org.jetbrains.kotlinx:kotlinx-coroutines-android", version.ref = "coroutines" }
                kotlinx-serialization-json = { module = "org.jetbrains.kotlinx:kotlinx-serialization-json", version.ref = "kotlin-serialization-json" }
                androidx-activity-compose = { module = "androidx.activity:activity-compose", version.ref = "androidx-activity" }
                androidx-lifecycle-viewmodel = { module = "org.jetbrains.androidx.lifecycle:lifecycle-viewmodel", version.ref = "androidx-lifecycle" }
                androidx-lifecycle-runtimeCompose = { module = "org.jetbrains.androidx.lifecycle:lifecycle-runtime-compose", version.ref = "androidx-lifecycle" }
                androidx-navigation-compose = { group = "org.jetbrains.androidx.navigation", name = "navigation-compose", version.ref = "navigation-compose" }
                android-firebase-bom = { module = "com.google.firebase:firebase-bom", version.ref = "firebase-bom" }
                android-firebase-auth = { module = "com.google.firebase:firebase-auth" }
                android-firebase-analytics = { module = "com.google.firebase:firebase-analytics" }
                koin-bom = { module = "io.insert-koin:koin-bom", version.ref = "koin-bom" }
                koin-core = { module = "io.insert-koin:koin-core" }
                koin-compose = { module = "io.insert-koin:koin-compose" }
                koin-viewmodel = { module = "io.insert-koin:koin-compose-viewmodel" }
                koin-startup = { module = "io.insert-koin:koin-androidx-startup" }
                koin-android = { module = "io.insert-koin:koin-android" }
                compose-backhandler = { module = "org.jetbrains.compose.ui:ui-backhandler", version.ref = "composeMultiplatform" }
                play-services-auth = { module = "com.google.android.gms:play-services-auth", version.ref = "playServicesAuth" }
                material-icons = { module = "org.jetbrains.compose.material:material-icons-extended", version.ref = "materialIcons"}

                [plugins]
                androidApplication = { id = "com.android.application", version.ref = "agp" }
                androidLibrary = { id = "com.android.library", version.ref = "agp" }
                composeMultiplatform = { id = "org.jetbrains.compose", version.ref = "composeMultiplatform" }
                composeCompiler = { id = "org.jetbrains.kotlin.plugin.compose", version.ref = "kotlin" }
                kotlinMultiplatform = { id = "org.jetbrains.kotlin.multiplatform", version.ref = "kotlin" }
                googleServices = { id = "com.google.gms.google-services", version.ref = "google-services-plugin" }
                kotlinCocoapods = { id = "org.jetbrains.kotlin.native.cocoapods", version.ref = "kotlin" }
                kotlinSerialization = { id = "org.jetbrains.kotlin.plugin.serialization", version.ref = "kotlin" }
                
                plugins {
                    // this is necessary to avoid the plugins to be loaded multiple times
                    // in each subproject's classloader
                    alias(libs.plugins.androidApplication) apply false
                    alias(libs.plugins.androidLibrary) apply false
                    alias(libs.plugins.composeMultiplatform) apply false
                    alias(libs.plugins.composeCompiler) apply false
                    alias(libs.plugins.kotlinMultiplatform) apply false
                    alias(libs.plugins.googleServices) apply false
                    alias(libs.plugins.kotlinCocoapods) apply false
                    alias(libs.plugins.kotlinSerialization) apply false
                }
                
                import org.jetbrains.kotlin.gradle.ExperimentalKotlinGradlePluginApi
                import org.jetbrains.kotlin.gradle.dsl.JvmTarget

                plugins {
                    alias(libs.plugins.kotlinMultiplatform)
                    alias(libs.plugins.androidApplication)
                    alias(libs.plugins.composeMultiplatform)
                    alias(libs.plugins.composeCompiler)
                    alias(libs.plugins.googleServices)
                    alias(libs.plugins.kotlinCocoapods)
                    alias(libs.plugins.kotlinSerialization)
                }

                kotlin {
                    androidTarget {
                        @OptIn(ExperimentalKotlinGradlePluginApi::class)
                        compilerOptions {
                            jvmTarget.set(JvmTarget.JVM_11)
                        }
                    }

                    iosX64()
                    iosArm64()
                    iosSimulatorArm64()

                    cocoapods {
                        summary = "My compose multiplatform app"
                        version = "1.0"
                        homepage = "https://medium.com"
                        ios.deploymentTarget = "16.0"

                        framework {
                            baseName = "ComposeApp"
                            isStatic = true
                        }

                        pod("FirebaseCore") {
                            version = "~> 11.13"
                            extraOpts += listOf("-compiler-option", "-fmodules")
                        }

                        pod("FirebaseAuth") {
                            version = "~> 11.13"
                            extraOpts += listOf("-compiler-option", "-fmodules")
                        }
                    }
                    
                    sourceSets {
                        androidMain.dependencies {
                            implementation(compose.preview)
                            implementation(libs.androidx.activity.compose)
                            implementation(project.dependencies.platform(libs.android.firebase.bom))
                            implementation(libs.android.firebase.auth)
                            implementation(libs.android.firebase.analytics)
                            implementation(libs.android.firebase.auth)
                            implementation(libs.android.firebase.analytics)
                            implementation(libs.kotlinx.coroutines.android)
                            implementation(libs.play.services.auth)
                            implementation(libs.koin.startup)
                            implementation(libs.koin.android)
                        }
                        commonMain.dependencies {
                            implementation(compose.runtime)
                            implementation(compose.foundation)
                            implementation(compose.material3)
                            implementation(compose.ui)
                            implementation(compose.components.resources)
                            implementation(compose.components.uiToolingPreview)
                            implementation(libs.androidx.lifecycle.viewmodel)
                            implementation(libs.androidx.lifecycle.runtimeCompose)
                            implementation(project.dependencies.platform(libs.koin.bom))
                            implementation(libs.koin.core)
                            implementation(libs.koin.viewmodel)
                            implementation(libs.koin.compose)
                            implementation(libs.kotlinx.coroutines.core)
                            implementation(libs.androidx.navigation.compose)
                            implementation(libs.kotlinx.serialization.json)
                            implementation(libs.compose.backhandler)
                            implementation(libs.material.icons)
                        }
                        commonTest.dependencies {
                            implementation(libs.kotlin.test)
                        }
                    }
                }

                android {
                    namespace = "com.medium.authtutorial"
                    compileSdk = libs.versions.android.compileSdk.get().toInt()

                    defaultConfig {
                        applicationId = "com.medium.authtutorial"
                        minSdk = libs.versions.android.minSdk.get().toInt()
                        targetSdk = libs.versions.android.targetSdk.get().toInt()
                        versionCode = 1
                        versionName = "1.0"
                    }
                    packaging {
                        resources {
                            excludes += "/META-INF/{AL2.0,LGPL2.1}"
                        }
                    }
                    buildTypes {
                        getByName("debug") {
                            isMinifyEnabled = false
                            applicationIdSuffix = ".dev"
                            versionNameSuffix = "-DEBUG"
                        }
                        getByName("release") {
                            isMinifyEnabled = false
                        }
                    }
                    compileOptions {
                        sourceCompatibility = JavaVersion.VERSION_11
                        targetCompatibility = JavaVersion.VERSION_11
                    }
                }

                dependencies {
                    debugImplementation(compose.uiTooling)
                }
                
                //commonMain
                fun initKoin(config: KoinAppDeclaration? = null) {
                    startKoin {
                        config?.invoke(this)
                        modules(sharedModule)
                    }
                }
                
                val sharedModule = module {
                    singleOf(::FirebaseServiceImpl).bind<FirebaseService>()
                }   
                
                interface FirebaseService {
                    fun getCurrentApp(): FirebaseAppInfo
                    fun getCurrentUser(): AppUser?
                    suspend fun signIn(email: String, password: String): AppUser
                    suspend fun setDisplayName(displayName: String)
                    suspend fun createUser(email: String, password: String, displayName: String): AppUser
                    suspend fun signOut()
                }

                data class FirebaseAppInfo(
                    val projectName: String,
                    val projectId: String
                ) {
                    override fun toString(): String =
                        "$projectName ($projectId)"
                }

                data class AppUser(
                    val userId: String,
                    val displayName: String,
                    val email: String
                ) {
                    fun greeting(): String =
                        "Hello, $displayName! Your email is $email and your user ID is $userId."
                }

                class FirebaseServiceImpl : FirebaseService {
                    override fun getCurrentApp(): FirebaseAppInfo =
                        getCurrentFirebaseApp()

                    override suspend fun signIn(email: String, password: String): AppUser =
                        signInWithFirebase(
                            email = email,
                            password = password
                        )

                    override suspend fun setDisplayName(displayName: String) =
                        setFirebaseUserDisplayName(
                            displayName = displayName
                        )

                    override suspend fun createUser(email: String, password: String, displayName: String): AppUser =
                        createFirebaseUser(
                            email = email,
                            password = password,
                            displayName = displayName
                        )

                    override fun getCurrentUser(): AppUser? =
                        getCurrentFirebaseUser()

                    override suspend fun signOut() =
                        signOutFirebaseUser()
                }
                
                expect fun getCurrentFirebaseApp(): FirebaseAppInfo
                expect fun getCurrentFirebaseUser(): AppUser?
                expect suspend fun signOutFirebaseUser()
                expect suspend fun signInWithFirebase(email: String, password: String): AppUser
                expect suspend fun setFirebaseUserDisplayName(displayName: String)
                expect suspend fun createFirebaseUser(email: String, password: String, displayName: String): AppUser
                
                //androidMain
                //MainApplication.kt
                import android.app.Application
                import com.medium.authtutorial.di.sharedModule
                import org.koin.android.ext.koin.androidContext
                import org.koin.androix.startup.KoinStartup
                import org.koin.core.annotation.KoinExperimentalAPI
                import org.koin.dsl.koinConfiguration

                @OptIn(KoinExperimentalAPI::class)
                class MainApplication : Application(), KoinStartup {

                    override fun onKoinStartup() = koinConfiguration {
                        androidContext(this@MainApplication)
                        modules(sharedModule)
                    }
                }
                
                //MainActivity.kt
                import android.os.Bundle
                import androidx.activity.ComponentActivity
                import androidx.activity.compose.setContent
                import androidx.activity.enableEdgeToEdge
                import androidx.compose.runtime.Composable
                import androidx.compose.ui.tooling.preview.Preview

                class MainActivity : ComponentActivity() {
                    override fun onCreate(savedInstanceState: Bundle?) {
                        enableEdgeToEdge()
                        super.onCreate(savedInstanceState)
                        setContent {
                            App()
                        }
                    }
                }

                @Preview
                @Composable
                fun AppAndroidPreview() {
                    App()
                }
                
                //FirebaseService.android.kt
                import com.google.firebase.FirebaseApp
                import com.google.firebase.auth.FirebaseAuth
                import com.google.firebase.auth.FirebaseUser
                import com.google.firebase.auth.UserProfileChangeRequest
                import kotlinx.coroutines.tasks.await

                actual fun getCurrentFirebaseApp(): FirebaseAppInfo {
                    return FirebaseAppInfo(
                        projectName = FirebaseApp.getInstance().name,
                        projectId = FirebaseApp.getInstance().options.projectId!!
                    )
                }

                actual suspend fun signInWithFirebase(
                    email: String,
                    password: String
                ): AppUser {
                    val authResult = FirebaseAuth.getInstance().signInWithEmailAndPassword(email, password).await()
                    val user = authResult.user
                    if (user != null) {
                        return user.toAppUser()
                    } else {
                        throw Exception("User not found")
                    }
                }

                actual suspend fun setFirebaseUserDisplayName(displayName: String) {
                    val user = FirebaseAuth.getInstance().currentUser
                    if (user != null) {
                        val profileUpdates = UserProfileChangeRequest.Builder()
                            .setDisplayName(displayName)
                            .build()
                        user.updateProfile(profileUpdates).await()
                    } else {
                        throw Exception("User not found")
                    }
                }

                actual suspend fun createFirebaseUser(
                    email: String,
                    password: String,
                    displayName: String
                ): AppUser {
                    val authResult =
                        FirebaseAuth.getInstance().createUserWithEmailAndPassword(email, password).await()
                    val user = authResult.user
                    if (user != null) {
                        val profileUpdates = UserProfileChangeRequest.Builder()
                            .setDisplayName(displayName)
                            .build()
                        user.updateProfile(profileUpdates).await()
                        return user.toAppUser()
                    } else {
                        throw Exception("User creation failed")
                    }
                }

                actual fun getCurrentFirebaseUser(): AppUser? =
                    FirebaseAuth.getInstance().currentUser?.toAppUser()

                private fun FirebaseUser.toAppUser(): AppUser =
                    AppUser(
                        userId = uid,
                        displayName = displayName ?: "Unknown",
                        email = email ?: "Unknown"
                    )

                actual suspend fun signOutFirebaseUser() {
                    // TODO: Listen to auth state changes, we are assuming the user is signed out here
                    FirebaseAuth.getInstance().signOut()
                }
                
                //iosApp
                //AppDelegate.swift
                import Foundation
                import UIKit
                import ComposeApp

                class AppDelegate: NSObject, UIApplicationDelegate {

                    func application(_ application: UIApplication,
                                     didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey : Any]? = nil) -> Bool {

                        print("AppDelegate: didFinishLaunchingWithOptions - Calling KMP init.")

                        // --- Call your KMP Initializer HERE ---
                        KMPInitializerKt.onDidFinishLaunchingWithOptions()

                        return true
                    }
                    // You can add other AppDelegate methods here if needed (e.g., for push notifications)
                }
                
                //ComposeView.swift
                struct ComposeView: UIViewControllerRepresentable {
                    func makeUIViewController(context: Context) -> UIViewController {
                        MainViewControllerKt.MainViewController()
                    }

                    func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                }

                struct ContentView: View {
                    var body: some View {
                        ComposeView()
                                .ignoresSafeArea(.keyboard) // Compose has own keyboard handler
                    }
                }
                
                //iosMain
                //KoinHelper.kt
                import org.koin.core.context.startKoin

                fun initKoin() = startKoin {
                    modules(
                        sharedModule
                    )
                }
                
                //KMPInitializer.kt
                import cocoapods.FirebaseCore.FIRApp
                import kotlinx.cinterop.ExperimentalForeignApi

                @OptIn(ExperimentalForeignApi::class)
                fun onDidFinishLaunchingWithOptions() {
                    println("KMP Initializer: Starting setup...")
                    FIRApp.configure() // Call Firebase configure
                    println("KMP Initializer: Firebase Configured.")
                }
                
                //MainViewController.kt
                import androidx.compose.ui.window.ComposeUIViewController
                import com.medium.authtutorial.di.initKoin

                fun MainViewController() = ComposeUIViewController(
                    configure = { initKoin() },
                    content = {
                        App()
                    }
                )
                
                //FirebaseService.ios.kt
                import cocoapods.FirebaseAuth.FIRAuth
                import cocoapods.FirebaseAuth.FIRUser
                import cocoapods.FirebaseCore.FIRApp
                import kotlinx.cinterop.BetaInteropApi
                import kotlinx.cinterop.CPointer
                import kotlinx.cinterop.ExperimentalForeignApi
                import kotlinx.cinterop.ObjCObjectVar
                import kotlinx.cinterop.alloc
                import kotlinx.cinterop.memScoped
                import kotlinx.cinterop.pointed
                import kotlinx.cinterop.ptr
                import kotlinx.cinterop.value
                import kotlinx.coroutines.suspendCancellableCoroutine
                import platform.Foundation.NSError
                import kotlin.coroutines.resume
                import kotlin.coroutines.resumeWithException

                @OptIn(ExperimentalForeignApi::class)
                actual fun getCurrentFirebaseApp(): FirebaseAppInfo {
                    return FirebaseAppInfo(
                        projectName = FIRApp.defaultApp()!!.name,
                        projectId = FIRApp.defaultApp()!!.options.projectID()!!
                    )
                }

                @OptIn(ExperimentalForeignApi::class)
                actual suspend fun signInWithFirebase(
                    email: String,
                    password: String
                ): AppUser = suspendCancellableCoroutine { continuation ->
                    FIRAuth.auth()
                        .signInWithEmail(email = email, password = password, completion = { authResult, error ->
                            if (error != null) {
                                continuation.resumeWithException(Exception(error.localizedDescription))
                            } else {
                                val user = authResult?.user()
                                if (user != null) {
                                    continuation.resume(user.toAppUser())
                                } else {
                                    continuation.resumeWithException(Exception("User not found"))
                                }
                            }
                        })
                }

                @OptIn(ExperimentalForeignApi::class)
                actual suspend fun setFirebaseUserDisplayName(displayName: String) =
                    suspendCancellableCoroutine { continuation ->
                        val user = FIRAuth.auth().currentUser()
                        if (user != null) {
                            val changeRequest = user.profileChangeRequest()
                            changeRequest.setDisplayName(displayName)
                            changeRequest.commitChangesWithCompletion { error ->
                                if (error != null) {
                                    continuation.resumeWithException(Exception(error.localizedDescription))
                                } else {
                                    continuation.resume(Unit)
                                }
                            }
                        } else {
                            continuation.resumeWithException(Exception("User not found"))
                        }
                    }

                @OptIn(ExperimentalForeignApi::class)
                actual suspend fun createFirebaseUser(
                    email: String,
                    password: String,
                    displayName: String
                ): AppUser = suspendCancellableCoroutine { continuation ->
                    FIRAuth.auth()
                        .createUserWithEmail(email = email, password = password, completion = { authResult, error ->
                            if (error != null) {
                                continuation.resumeWithException(Exception(error.localizedDescription))
                            } else {
                                val user = authResult?.user()
                                if (user != null) {
                                    val changeRequest = user.profileChangeRequest()
                                    changeRequest.setDisplayName(displayName)
                                    changeRequest.commitChangesWithCompletion { error ->
                                        if (error != null) {
                                            continuation.resumeWithException(Exception(error.localizedDescription))
                                        } else {
                                            continuation.resume(user.toAppUser())
                                        }
                                    }
                                } else {
                                    continuation.resumeWithException(Exception("User not found"))
                                }
                            }
                        })
                }

                @OptIn(ExperimentalForeignApi::class, BetaInteropApi::class)
                actual suspend fun signOutFirebaseUser() = suspendCancellableCoroutine { continuation ->
                    memScoped {
                        val errorPointer: CPointer<ObjCObjectVar<NSError?>> = alloc<ObjCObjectVar<NSError?>>().ptr
                        val wasSuccessful = FIRAuth.auth().signOut(errorPointer)
                        if (!wasSuccessful) {
                            val error = errorPointer.pointed.value
                            if (error != null) {
                                continuation.resumeWithException(Exception(error.localizedDescription))
                            } else {
                                continuation.resumeWithException(Exception("Unknown error occurred during sign out"))
                            }
                        } else {
                            continuation.resume(Unit)
                        }
                    }
                }
                
                @OptIn(ExperimentalForeignApi::class)
                actual fun getCurrentFirebaseUser(): AppUser? =
                FIRAuth.auth().currentUser()?.toAppUser()
                
                @OptIn(ExperimentalForeignApi::class)
                private fun FIRUser.toAppUser(): AppUser =
                    AppUser(
                        userId = uid(),
                        displayName = displayName() ?: "Unknown",
                        email = email() ?: "Unknown"
                    )   
            """.trimIndent()
        )

        KotlinCode(
            code = """
                    //Jetpack compose
                    
                    import com.google.firebase.firestore.DocumentReference
                    import com.google.firebase.firestore.DocumentSnapshot
                    import com.google.firebase.firestore.EventListener
                    import com.google.firebase.firestore.FirebaseFirestoreException
                    import com.google.firebase.firestore.Query
                    import kotlinx.coroutines.cancel
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow

                    fun <T> Query.snapshotFlow(dataType: Class<T>): Flow<List<T>> = callbackFlow {
                        val listenerRegistration = addSnapshotListener { value, error ->
                            if (error != null) {
                                trySend(emptyList())
                                close()
                                return@addSnapshotListener
                            }
                            if (value != null) {
                                val data = value.documents.filter { it.exists() }.mapNotNull { it.toObject(dataType) as? T }
                                trySend(data)
                            } else {
                                trySend(emptyList())
                            }
                        }
                        awaitClose {
                            listenerRegistration.remove()
                        }
                    }

                    fun <T> DocumentReference.snapshotFlow(dataType: Class<T>): Flow<T?> = callbackFlow {
                        val listener = object : EventListener<DocumentSnapshot> {
                            override fun onEvent(snapshot: DocumentSnapshot?, exception: FirebaseFirestoreException?) {
                                if (exception != null) {
                                    cancel()
                                    return
                                }

                                if (snapshot != null && snapshot.exists()) {
                                    val data = snapshot.toObject(dataType)
                                    trySend(data)
                                } else {
                                    trySend(null)
                                }
                            }
                        }

                        val registration = addSnapshotListener(listener)
                        awaitClose { registration.remove() }
                    }
                    """.trimIndent()
        )
    }
}

/*
    suspend fun updateIndex(indexData: String): Flow<Response<Any>> = networkFlow {
        val url = URLBuilder(protocol = URLProtocol.HTTPS).apply {
            host = "firestore.googleapis.com"
            path("v1/projects/bluelabs-41aef/databases/(default)/documents/index/blog-index")
            parameters.append("key", BuildKonfig.FIREBASE_AUTH_TOKEN)
        }.build()

        val requestBody = mapOf(
            "fields" to mapOf(
                "index" to mapOf(
                    "stringValue" to indexData
                )
            )
        )

        httpClient.patch(url) {
            contentType(ContentType.Application.Json)
            setBody(requestBody)
        }.getResponse<Any>()
    }
*/

//https://github.com/larkes-cyber/KmpFirebaseAppSample.git
//https://github.com/akobartek/SMWolczyn.git
//https://github.com/CagriOrcan/ChristmasGrapes //supabase
//https://github.com/anantmittal943/EcellKMP.git
//https://github.com/EEseka/ShelfLife (safeFirebaseAuthCall, platformSafeCall KTOR, Notification, PickedImage)
//https://medium.com/@ilyas.savli47/integrating-firebase-firestore-into-a-compose-multiplatform-project-without-cocoapods-3f00eb82d828
//https://github.com/jasi381/FirebaseAuthKMP